class InputRange extends DummyElement {
  #input;
  #dropdown;

  constructor() {
    super();

    this.setAttribute("data-property", this.label);

    const shadow = this.attachShadow({ mode: "closed" });
    const container = document.createElement("div");
    // container.classList.add("w-full", "my-15");
    container.classList.add("w-full");
    const label = document.createElement("label");
    const span = document.createElement("span");
    span.innerText = this.label;
    const input = document.createElement("input");
    this.#input = input;
    input.setAttribute("autocomplete", "off");
    input.classList.add("flex", "flex-wrap");

    if (this.autocomplete) {
      const dropdown = document.createElement("input-dropdown");
      this.#dropdown = dropdown;
      this.#dropdown.setAttribute("postfix", this.postfix);
      this.#dropdown.addEventListener(
        "change",
        this.#handleDropdownChange.bind(this)
      );
      container.appendChild(dropdown);
    }

    label.appendChild(span);
    label.appendChild(input);
    container.appendChild(label);

    if (this.hint) {
      const hint = document.createElement("p");
      hint.classList.add("hint");
      hint.innerText = this.hint;
      container.appendChild(hint);
      this.autocomplete && this.#dropdown.setAttribute("hint", "");
    }

    shadow.appendChild(container);

    const style = document.createElement("style");
    style.textContent = this.#generateStyle();

    shadow.appendChild(style);

    this.#generateSuggestions.bind(this);

    this.#input.addEventListener("input", this.#handleInput.bind(this));
    this.#input.addEventListener("focus", this.#handleInputFocus.bind(this));
    this.#input.addEventListener("blur", this.#handleInputBlur.bind(this));
    this.#input.addEventListener(
      "keydown",
      this.#handleInputKeydown.bind(this)
    );

    if (this.defaultValue) {
      this.value = this.defaultValue;
      this.#input.value = this.value;
    }
  }

  connectedCallback() {
    PriceRenderer.registerSymbol(this.name, this.min);
  }

  disconnectedCallback() {
    this.#input.removeEventListener("input", this.#handleInput);
    this.#input.removeEventListener("focus", this.#handleInputFocus);
    this.#input.removeEventListener("blur", this.#handleInputBlur);
    this.#input.removeEventListener("keydown", this.#handleInputKeydown);
  }

  get label() {
    return this.getAttribute("label");
  }

  get min() {
    return parseFloat(this.getAttribute("min"));
  }

  get max() {
    return parseFloat(this.getAttribute("max"));
  }

  get step() {
    return Math.round(parseFloat(this.getAttribute("step")) * 100) / 100;
  }

  get postfix() {
    return this.getAttribute("postfix");
  }

  get hint() {
    return this.getAttribute("hint");
  }

  get autocomplete() {
    return this.hasAttribute("autocomplete");
  }

  get suggestions() {
    return (
      this.autocomplete && (parseInt(this.getAttribute("suggestions")) || 5)
    );
  }

  get name() {
    return this.getAttribute("name");
  }

  get value() {
    return this.#input.value;
  }

  set value(newValue) {
    let currentValue = newValue.replace(this.postfix, "") || 0;
    currentValue = isNaN(currentValue) ? 0 : parseFloat(currentValue);
    if (currentValue <= this.min) {
      currentValue = this.min;
    } else if (currentValue >= this.max) {
      currentValue = this.max;
    }
    const base = currentValue - this.min;
    if (
      Math.round((base % this.step) * 100) / 100 !==
      Math.round((this.step - 1e-100) * 100) / 100
    ) {
      currentValue = Math.floor(base / this.step) * this.step + this.min;
    }
    currentValue = Math.round(currentValue * 100) / 100;
    this.#input.value = currentValue + this.postfix;
    PriceRenderer.modifySymbol(this.name, currentValue);
  }

  #handleInput() {
    this.#dropdown?.setAttribute("options", this.#generateSuggestions());
  }

  #handleInputFocus() {
    this.#input.setSelectionRange(
      0,
      this.#input.value.length - this.postfix.length
    );
    this.#dropdown?.setAttribute("open", "");
    this.#dropdown?.setAttribute("options", this.#generateSuggestions());
  }

  #handleInputBlur({ currentTarget: { value }, relatedTarget }) {
    if (relatedTarget !== this.#dropdown) {
      this.value = value;
      this.#dropdown?.removeAttribute("open");
    }
  }

  #handleInputKeydown({ keyCode, currentTarget }) {
    if (keyCode === 13) {
      currentTarget.blur();
    }
  }

  #handleDropdownChange({ detail: { value } }) {
    this.value = value;
    this.#dropdown?.removeAttribute("open");
  }

  #generateSuggestions(n = this.suggestions) {
    if (n === 0) {
      return [];
    }

    let result = [];
    let currentValue = this.#input.value.replace(this.postfix, "") || 0;
    currentValue = isNaN(currentValue) ? 0 : parseFloat(currentValue);
    if (currentValue < 1) {
      return [];
    }

    if (currentValue <= this.min) {
      result.push(this.min);
    }

    for (let i = result.length; i < n; i++) {
      const suggest =
        Math.floor(
          (`${currentValue + this.step * (i - 1)}` - this.min) / this.step
        ) *
          this.step +
        this.min;
      if (suggest <= this.min) {
        currentValue = parseFloat(currentValue) || 1;
        if (("" + currentValue).length < ("" + this.min).length) {
          currentValue *= 10;
        } else {
          currentValue += this.step;
        }
        i--;
      } else if (suggest <= this.max) {
        result.push(Math.round(suggest * 100) / 100);
      }
    }
    return result;
  }

  #generateStyle() {
    return `
    * {
        box-sizing: border-box;
    }
    :host {
        display: block;
        position: relative;
    }
    :host(:focus-within) {
        z-index: 20;
    }
    .w-full{
        width: 100%;
        position: relative;
    }
    .my-15 {
        margin-top: 1.5rem;
        margin-bottom: 1.5rem;
    }
    .flex-wrap {
        flex-wrap: wrap;
    }
    .flex {
        display: flex;
    }
    input {
        display:block;
        appearance: none;
        background-color: #fff;
        border: 1px solid #c7c7c7;
        border-radius: 3px;
        cursor: pointer;
        line-height: 1.25;
        padding: 0.75rem 1.5rem;
        color: #22292f;
        width: 100%;
        padding: 0.75rem 1.5rem 0.75rem 1.3rem;
        font-size: 1.5rem;
        font-weight: 400;
        ${
          this.autocomplete
            ? `background-image: url(data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMTggMTAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTAgMWwxLTEgOCA4IDgtOCAxIDEtOSA5eiIgZmlsbC1ydWxlPSJldmVub2RkIi8+PC9zdmc+);`
            : ""
        }
        background-position: right 1em center;
        background-size: 1em;
        background-repeat: no-repeat;
    }
    label  {
        display: block;
        font-weight: 500;
        margin-top: 0;
        position: relative;
        color: #101010;
        font-size: 1.3rem;
    }
    label span {
        display:block;
        margin-bottom: 0.5rem;
    }
    .hint {
      color: #909090;
      font-size: 1.1rem;
      line-height: 1.4;
      margin: .5rem 0 1rem;
    }
    `;
  }
}

customElements.define("input-range", InputRange);
